import { z } from "zod";
import type { GameTypeCode, Hint } from "@monekineko/game-engine";

// Контракты API. Схемы работают и как валидация на Fastify, и как типы у клиента.

// ---------- Игрок ----------

// Вход разбираем мягко: пустые или неожиданные данные должны дойти до проверки
// подписи и вернуться игроку понятным текстом, а не отказом схемы «не прошло
// проверку», по которому не видно, что делать.
export const authTelegramSchema = z.object({
  initData: z.string().catch(""),
  startParam: z.string().max(64).optional().catch(undefined),
  /** С чем открылось мини-приложение: уходит в журнал, когда вход не удался */
  launch: z
    .object({
      sdk: z.boolean(),
      params: z.array(z.string().max(40)).max(20),
      platform: z.string().max(32),
      version: z.string().max(16),
    })
    .optional()
    .catch(undefined),
});

export const checkLevelSchema = z.object({
  solution: z.unknown(),
});

export const finishLevelSchema = z.object({
  solution: z.unknown(),
  /** Сколько ошибок насчитал клиент — только для аналитики, звёзды считаются сервером */
  clientMistakes: z.number().int().min(0).max(9999).optional(),
});

export const gameEventSchema = z.object({
  type: z.string().min(1).max(32),
  payload: z.record(z.string(), z.unknown()).optional(),
  at: z.number().int().optional(),
});

export const gameEventsSchema = z.object({
  events: z.array(gameEventSchema).max(200),
});

export const sessionPingSchema = z.object({
  sessionId: z.string().uuid().optional(),
  platform: z.string().max(32).optional(),
});

export const supportMessageSchema = z.object({
  text: z.string().min(1).max(2000),
});

export interface PlayerProfile {
  id: string;
  telegramId: string;
  firstName: string;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
  lives: number;
  livesMax: number;
  /** Секунд до следующей жизни, null — запас полон */
  nextLifeIn: number | null;
  hints: number;
  points: number;
  stars: number;
  levelsCompleted: number;
  invitedCount: number;
  qualifiedCount: number;
  referralCode: string;
  referralLink: string;
  achievements: { code: string; title: string; description: string; grantedAt: string }[];
}

export interface MapLevel {
  id: string;
  order: number;
  title: string;
  gameType: GameTypeCode;
  status: "locked" | "available" | "completed";
  stars: number;
  bestDurationMs: number | null;
  attempts: number;
  introText: string | null;
  unlockHint: string | null;
}

export interface MapChapter {
  id: string;
  order: number;
  title: string;
  subtitle: string | null;
  description: string | null;
  coverUrl: string | null;
  status: "locked" | "available" | "completed";
  unlockHint: string | null;
  levels: MapLevel[];
}

export interface MapResponse {
  chapters: MapChapter[];
  currentLevelId: string | null;
}

export interface LevelStartResponse {
  instanceId: string;
  levelId: string;
  gameType: GameTypeCode;
  title: string;
  introText: string | null;
  /** Показывать ли кат-сцену: первый заход на уровень */
  playIntro: boolean;
  /** Всё, что нужно нарисовать сцену. Настройки уровня остаются на сервере:
      у некоторых типов в них лежит правильный ответ. */
  payload: unknown;
  hintsAvailable: number;
  lives: number;
  serverTime: number;
}

export interface LevelFinishResponse {
  solved: boolean;
  reason?: string;
  stars: number;
  points: number;
  pointsTotal: number;
  durationMs: number;
  mistakes: number;
  lives: number;
  nextLevelId: string | null;
  chapterCompleted: boolean;
  outroText: string | null;
  newAchievements: { code: string; title: string; description: string }[];
  details?: Record<string, unknown>;
}

/** Ответ на проверку решения без зачёта: верно или разбор ошибки */
export interface LevelCheckResponse {
  solved: boolean;
  reason?: string;
  attempts: number;
  details?: Record<string, unknown>;
}

export interface HintResponse {
  hint: Hint;
  hintsLeft: number;
}

export interface LeaderboardRow {
  rank: number;
  name: string;
  photoUrl: string | null;
  points: number;
  stars: number;
  isMe: boolean;
}

export interface LeaderboardResponse {
  period: "week" | "all";
  rows: LeaderboardRow[];
  me: LeaderboardRow | null;
}

export interface FriendsResponse {
  referralCode: string;
  referralLink: string;
  shareText: string;
  invited: { name: string; joinedAt: string; qualified: boolean; levelsCompleted: number }[];
  rewards: { lives: number; hints: number; points: number };
  referrer: { name: string } | null;
  unlockPerInvites: number;
  invitesToNextUnlock: number | null;
}

// ---------- Админка ----------

export const adminLoginSchema = z.object({
  login: z.string().min(2).max(64),
  password: z.string().min(4).max(128),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(4).max(128),
  newPassword: z.string().min(8).max(128),
});

export const segmentSchema = z.object({
  chapterId: z.string().nullish(),
  levelId: z.string().nullish(),
  minLevelsCompleted: z.number().int().min(0).nullish(),
  maxLevelsCompleted: z.number().int().min(0).nullish(),
  registeredFrom: z.string().nullish(),
  registeredTo: z.string().nullish(),
  /** Не заходил столько дней */
  idleDays: z.number().int().min(0).max(365).nullish(),
  /** Заходил за последние столько дней */
  activeDays: z.number().int().min(0).max(365).nullish(),
  hasReferrals: z.boolean().nullish(),
  referrerId: z.string().nullish(),
  includeBlocked: z.boolean().default(false),
  minPoints: z.number().int().min(0).nullish(),
  status: z.enum(["active", "banned"]).nullish(),
});

export type Segment = z.infer<typeof segmentSchema>;

export const broadcastButtonSchema = z.object({
  text: z.string().min(1).max(64),
  type: z.enum(["url", "webapp"]),
  value: z.string().min(1).max(512),
});

export const broadcastMessageSchema = z.object({
  text: z.string().min(1).max(4096),
  parseMode: z.enum(["HTML", "MarkdownV2", "none"]).default("HTML"),
  mediaId: z.string().nullish(),
  buttons: z.array(broadcastButtonSchema).max(6).default([]),
});

export type BroadcastMessage = z.infer<typeof broadcastMessageSchema>;

export const broadcastSchema = z.object({
  title: z.string().min(1).max(200),
  segment: segmentSchema,
  message: broadcastMessageSchema,
  scheduledAt: z.string().nullish(),
});

export const chapterSchema = z.object({
  title: z.string().min(1).max(200),
  subtitle: z.string().max(300).nullish(),
  description: z.string().max(4000).nullish(),
  coverMediaId: z.string().nullish(),
  unlockRule: z
    .object({
      previousChapter: z.boolean().default(true),
      minInvites: z.number().int().min(0).default(0),
      availableFrom: z.string().nullish(),
    })
    .default({ previousChapter: true, minInvites: 0 }),
  isPublished: z.boolean().default(false),
});

export const levelSchema = z.object({
  chapterId: z.string(),
  gameType: z.string(),
  title: z.string().min(1).max(200),
  introText: z.string().max(2000).nullish(),
  outroText: z.string().max(2000).nullish(),
  settings: z.record(z.string(), z.unknown()),
  starThresholds: z.record(z.string(), z.unknown()).optional(),
  pointsReward: z.number().int().min(0).max(10000).default(100),
  variantMode: z.enum(["generated", "fixed"]).default("generated"),
  fixedVariantId: z.string().nullish(),
  isPublished: z.boolean().default(false),
});

export const reorderSchema = z.object({
  ids: z.array(z.string()).min(1),
});

export const adminUserSchema = z.object({
  login: z.string().min(2).max(64),
  name: z.string().min(1).max(120),
  role: z.enum(["owner", "editor", "viewer"]),
  password: z.string().min(8).max(128).optional(),
  isActive: z.boolean().default(true),
});

export const grantSchema = z.object({
  lives: z.number().int().min(-99).max(99).optional(),
  hints: z.number().int().min(-99).max(99).optional(),
  points: z.number().int().min(-100000).max(100000).optional(),
  reason: z.string().max(300).optional(),
});

export const automationSchema = z.object({
  enabled: z.boolean(),
  message: broadcastMessageSchema,
  delayMinutes: z.number().int().min(0).max(100000),
  cooldownDays: z.number().int().min(0).max(365),
  threshold: z.number().int().min(0).max(1000).optional(),
});

export const textSchema = z.object({
  value: z.string().max(4096),
});
